interface CompletionDateProps {
  date: string;
  /**
   * Raw `status_normalized` bucket (e.g. "PENDING_SUBMISSION"), NOT the
   * human-readable label. Optional: when absent the pill falls back to
   * pure date-based colouring.
   */
  status?: string | null;
  italic?: boolean;
}

/**
 * Buckets where the facility has no work left to do. COMPLETED/CANCELLED/
 * STRUCK are terminal; PENDING_SUBMISSION covers EHR codes 11/12/17
 * ("Complete; Ready for Submission", "Placed in EDS Batch", "Pending
 * review by CMS") — those are finished and waiting on CMS, not on a
 * clinician, so an old date must never render as red/overdue.
 */
const FINISHED_BUCKETS = ["COMPLETED", "CANCELLED", "STRUCK", "PENDING_SUBMISSION"];

const isFinishedBucket = (status?: string | null): boolean =>
  !!status && FINISHED_BUCKETS.includes(status.trim().toUpperCase());

const getDaysUntilCompletion = (dateString: string) => {

  if (!dateString) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to start of day
  const completionDate = new Date(dateString);
  completionDate.setHours(0, 0, 0, 0);
  const diffTime = completionDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export function CompletionDate({ date, status, italic = false }: CompletionDateProps) {
  if (!date || date === "N/A") {
    return <span className={`text-muted-foreground ${italic ? "italic" : ""}`}>N/A</span>;
  }

  const daysUntil = getDaysUntilCompletion(date);

  if (daysUntil === null) {
    return <span className={italic ? "italic" : ""}>{date}</span>;
  }

  // Determine pill color based on days until completion. Finished buckets
  // short-circuit the date math entirely so completed work never nags.
  let bgColor = "bg-[var(--color-destructive)]"; // Red for 0-3 days
  if (isFinishedBucket(status)) {
    bgColor = "bg-[var(--color-success)]"; // Green - nothing left to do
  } else if (daysUntil >= 8) {
    bgColor = "bg-[var(--color-success)]"; // Green for 8+ days
  } else if (daysUntil >= 4) {
    bgColor = "bg-[var(--color-warning)]"; // Yellow for 4-7 days
  }

  return (
    <td className="p-3">
    <span className={`inline-flex items-center px-2 py-1 rounded-tag ${bgColor} text-white content-center justify-center ${italic ? "italic" : ""}`}>
      {date}
    </span>
    </td>
  );
}
